import type { ComposicaoPrecoResponse, FormacaoCalculadaResponse, MargemContribuicaoResponse, MarcaRevisaoResponse, OrigemLucro, ReformaFormacaoResponse, TributosPorOrigemResponse } from '@triade/contracts';
import type { AvisoTributario } from './tributos-origem';

/**
 * Leitura tipada de `resultado` e `snapshot` (vindos como `unknown` da API). Somente os campos que a tela exibe;
 * tudo e string decimal e nada aqui calcula.
 */
export interface ResultadoMotor {
  variableExpenseRate: string;
  fixedExpenseRate: string;
  desiredProfitRate: string;
  ipiRate: string;
  practiced: null | { simulatedProfit: string };
}

export interface AvisoMotor {
  code: string;
  message: string;
}

export interface SnapshotFormacao {
  company: { financialCostRate: string };
  refs: { costSource?: string; fichaVersao?: number | null; tabelaId?: string; condicaoId?: string };
}

export interface FormacaoLida {
  referenciaEm: string;
  motorVersao: string;
  resultado: ResultadoMotor;
  snapshot: SnapshotFormacao;
  avisos: AvisoMotor[];
  composicao: ComposicaoPrecoResponse;
  /** De onde vieram a meta e o minimo de lucro (DP6). */
  lucroDesejadoOrigem: OrigemLucro | undefined;
  lucroMinimoOrigem: OrigemLucro | undefined;
  /** Marca "custo atualizado — revisar preco" desta chave (ao vivo, da API); null quando nao ha. */
  marcaRevisao: MarcaRevisaoResponse | null;
  /** Margem de contribuicao (sugerido e praticado); undefined em API antiga. */
  margemContribuicao: MargemContribuicaoResponse | undefined;
  /** Reforma tributaria (6b): regime pela data, tributos por fora e total da NF; undefined em API antiga. */
  reforma: ReformaFormacaoResponse | undefined;
  /** Faixa do praticado decidida pelo motor (com a tolerancia de meio centavo no preco); undefined sem praticado ou em API antiga. */
  faixaPraticado: 'green' | 'orange' | 'red' | undefined;
  /** Sessao 51 (tabela ESTRUTURADA): tributos por origem (texto, R$, lucro no Real); undefined na tabela LEGADO. */
  tributos: TributosPorOrigemResponse | undefined;
  /** Avisos do perfil tributario (`avisosTributarios`); a tela os mescla com `tributos.avisos` sem repetir. */
  avisosTributarios: AvisoTributario[];
}

/** Valida a forma minima da resposta; sem `composicao` (API antiga) a tela trata como erro de contrato. */
export function lerFormacao(r: FormacaoCalculadaResponse): FormacaoLida | null {
  const resultado = r.resultado as ResultadoMotor | null;
  const snapshot = r.snapshot as SnapshotFormacao | null;
  if (!resultado || !snapshot || !r.composicao) return null;
  const avisos = (r.warnings as Array<Partial<AvisoMotor>>).flatMap((w) =>
    typeof w.code === 'string' ? [{ code: w.code, message: String(w.message ?? w.code) }] : [],
  );
  return { referenciaEm: r.referenciaEm, motorVersao: r.motorVersao, resultado, snapshot, avisos, composicao: r.composicao, lucroDesejadoOrigem: r.lucroDesejadoOrigem, lucroMinimoOrigem: r.lucroMinimoOrigem, marcaRevisao: r.marcaRevisao ?? null, margemContribuicao: r.margemContribuicao, reforma: r.reforma, faixaPraticado: r.faixaPraticado, tributos: r.tributosPorOrigem, avisosTributarios: r.avisosTributarios ?? [] };
}

/**
 * Liberacao por UF (Sessao 51b): a regra de derivacao vem da API (`porUf.derivacao`); aqui so o texto que explica a regra.
 * Nada e calculado: o preco de cada UF ja vem pronto em `porUf.ufs`.
 */
export function fraseDerivacaoUf(derivacao: 'LIQUIDO_DENTRO' | 'MESMO_LUCRO', ufOrigem: string | null): string {
  const de = ufOrigem ? `de ${ufOrigem}` : 'da UF de origem';
  return derivacao === 'LIQUIDO_DENTRO' ? `Derivado ${de}: mesmo preço líquido de tributos por dentro.` : `Derivado ${de}: mesmo lucro.`;
}

/** Busca da URL `/precificar/formacao?itemId=&tabelaId=&condicaoId=&praticado=`: so ids e decimal seguros; o resto e ignorado. */
export function lerBuscaFormacao(bruto: Record<string, unknown>): { itemId?: string; tabelaId?: string; condicaoId?: string; praticado?: string } {
  const id = (v: unknown) => {
    const t = typeof v === 'number' ? String(v) : v;
    return typeof t === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(t) ? t : undefined;
  };
  // Decimal com ponto (como vem da API, "25.00"): so o formato; a tela converte para o campo.
  const praticado = (v: unknown) => (typeof v === 'string' && /^\d{1,12}(\.\d{1,8})?$/.test(v) ? v : undefined);
  const itemId = id(bruto.itemId);
  const tabelaId = id(bruto.tabelaId);
  const condicaoId = id(bruto.condicaoId);
  const pratic = praticado(bruto.praticado);
  return {
    ...(itemId ? { itemId } : {}),
    ...(tabelaId ? { tabelaId } : {}),
    ...(condicaoId ? { condicaoId } : {}),
    ...(pratic ? { praticado: pratic } : {}),
  };
}
